import { supabase } from '../supabase'
import { PRUEBA_TABLE_MAP, TABLE_TO_TIPO } from '../../services/pruebasService'

export interface PruebaDato {
  nombre: string
  tabla: string
  data: Record<string, any>
  mostrarObs: boolean
  incluirReporteCliente: boolean
}

export interface DatosReporte {
  pedido: Record<string, any>
  pruebas: PruebaDato[]
}

// tipo_prueba (ej. 'A_3801') → tabla (ej. 'prueba_3801')
const TIPO_TO_TABLA: Record<string, string> = {}
for (const [tabla, tipo] of Object.entries(TABLE_TO_TIPO)) {
  TIPO_TO_TABLA[tipo] = tabla
}

// tabla (ej. 'prueba_3801') → nombre display (ej. 'NMX-A-3801-INNTEX-2012 ...')
const TABLA_TO_NOMBRE: Record<string, string> = {}
for (const [nombre, tabla] of Object.entries(PRUEBA_TABLE_MAP)) {
  if (!TABLA_TO_NOMBRE[tabla]) TABLA_TO_NOMBRE[tabla] = nombre
}

export async function fetchDatosReporte(
  pedidoId: string,
  opts?: { incluirNoAceptadas?: boolean },
): Promise<DatosReporte> {
  const { data: pedido } = await supabase
    .from('pedidos')
    .select('*')
    .eq('id', pedidoId)
    .single()

  // noms es la fuente de verdad: tiene aceptado y row_id_prueba para cada prueba del pedido.
  // El reporte oficial solo incluye pruebas ya aceptadas; la vista previa (incluirNoAceptadas)
  // muestra cualquier prueba con datos guardados, incluyendo borradores y "sin revisión".
  let query = supabase
    .from('noms')
    .select('tipo_prueba, row_id_prueba, mostrar_observaciones, incluir_reporte_cliente, orden')
    .eq('pedido_id', pedidoId)
    .order('orden', { ascending: true, nullsFirst: false })
  if (!opts?.incluirNoAceptadas) query = query.eq('aceptado', 'aceptado')
  const { data: noms } = await query

  // Promise.all resuelve en orden de finalización, no en el orden de `noms` — se indexa por
  // posición para conservar el orden manual (drag and drop) sin importar qué fetch termine primero.
  const resultados = await Promise.all(
    (noms || []).map(async nom => {
      const tabla = TIPO_TO_TABLA[nom.tipo_prueba]
      if (!tabla || !nom.row_id_prueba) return null
      const { data } = await supabase
        .from(tabla)
        .select('*')
        .eq('id', nom.row_id_prueba)
        .maybeSingle()
      if (!data) return null
      const nombre = TABLA_TO_NOMBRE[tabla] || nom.tipo_prueba
      return {
        nombre,
        tabla,
        data,
        mostrarObs: nom.mostrar_observaciones === true,
        incluirReporteCliente: nom.incluir_reporte_cliente !== false,
      } satisfies PruebaDato
    })
  )
  const pruebas = resultados.filter((p): p is PruebaDato => p !== null)

  return { pedido: pedido ?? {}, pruebas }
}
